<template>
  <b-modal
    ref="modal-promessas"
    title="Promessas de Pagamento"
    size="xl"
    scrollable
    @show="onShow"
    @hide="onHide">

    <!-- Filtros e contadores -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="filtros">
        <b-form-group label="Filtrar por status:" label-cols="auto" class="mb-0" label-class="label-filtro">
          <b-form-select
            v-model="filtroStatus"
            :options="opcoesStatus"
            class="select-filtro"
            style="min-width: 200px; font-size: 14px;">
          </b-form-select>
        </b-form-group>
      </div>

      <div class="badge-info" style="background-color: white; padding: 8px; border-radius: 4px;">
        <span class="badge badge-secondary mr-2">
          Total: {{ promessasFiltradas.length }}
        </span>
        <span class="badge badge-danger mr-2" v-if="contadores.quebradas > 0">
          Quebradas: {{ contadores.quebradas }}
        </span>
        <span class="badge badge-warning mr-2" v-if="contadores.vencendo > 0">
          Vencendo: {{ contadores.vencendo }}
        </span>
        <span class="badge badge-info mr-2" v-if="contadores.pendentes > 0">
          Pendentes: {{ contadores.pendentes }}
        </span>
        <span class="badge badge-success" v-if="contadores.cumpridas > 0">
          Cumpridas: {{ contadores.cumpridas }}
        </span>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="text-muted mt-2">Carregando promessas...</p>
    </div>

    <!-- Sem dados -->
    <div v-else-if="promessas.length === 0" class="text-center py-5">
      <i class="fas fa-handshake fa-3x text-muted mb-3"></i>
      <p class="text-muted">Nenhuma promessa de pagamento registrada</p>
    </div>

    <!-- Lista de promessas -->
    <div v-else-if="promessasFiltradas.length === 0" class="text-center py-5">
      <i class="fas fa-filter fa-3x text-muted mb-3"></i>
      <p class="text-muted">Nenhuma promessa encontrada com este filtro</p>
    </div>

    <!-- Tabela de promessas - MESMO ESTILO DA GRID ANALÍTICA -->
    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead style="background-color: #04a791; color: white;">
          <tr>
            <th style="vertical-align: middle;">Situação</th>
            <th style="vertical-align: middle;">Contrato</th>
            <th class="text-center" style="vertical-align: middle;">Parcela</th>
            <th class="text-right" style="vertical-align: middle;">Valor</th>
            <th class="text-center" style="vertical-align: middle;">Data Prometida</th>
            <th style="vertical-align: middle;">Responsável</th>
            <th style="vertical-align: middle;">Observações</th>
            <th class="text-center" style="vertical-align: middle;">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="promessa in promessasFiltradas" :key="promessa.promessaId">
            <td>
              <span :class="getStatusBadgeClass(promessa.statusVisual)">
                <i :class="getStatusIcon(promessa.statusVisual)"></i>
                {{ getStatusTexto(promessa.statusVisual) }}
              </span>
            </td>
            <td>{{ promessa.contratoCredito }}</td>
            <td class="text-center">
              <div style="display: flex; justify-content: center;">
                <span class="badge badge-info">{{ promessa.numeroParcela }}</span>
              </div>
            </td>
            <td class="text-right">
              <strong>{{ formatValue(promessa.valorAtualizado) }}</strong>
            </td>
            <td class="text-center">
              {{ formatDate(promessa.dataPagamentoPrometida) }}
              <small v-if="promessa.isVencendo" class="text-warning d-block">
                <i class="fas fa-clock"></i> Vencendo
              </small>
              <small v-else-if="promessa.isVencida" class="text-danger d-block">
                <i class="fas fa-exclamation-triangle"></i> Vencida
              </small>
            </td>
            <td>
              {{ promessa.nomeUsuarioResponsavel || promessa.usuarioResponsavel || '-' }}
            </td>
            <td>
              <span class="text-truncate d-block" style="max-width: 200px" :title="promessa.observacoes">
                {{ promessa.observacoes || '-' }}
              </span>
            </td>
            <td class="text-center">
              <div class="btn-group btn-group-sm">
                <!-- Botão Marcar como Cumprida - REMOVIDO (detecção automática) -->

                <!-- Botão Cancelar -->
                <b-button
                  v-if="promessa.statusVisual !== 'CUMPRIDA' && promessa.statusVisual !== 'CANCELADA'"
                  variant="outline-danger"
                  size="sm"
                  @click="cancelarPromessa(promessa)"
                  title="Cancelar promessa">
                  <i class="fas fa-times"></i>
                </b-button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Footer do modal -->
    <template #modal-footer>
      <b-button variant="secondary" @click="hideModal">
        Fechar
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import PromessaRepository from '@/shared/http/repositories/promessa/PromessaRepository'
import Tools from '@/components/tools/Tools.js'
import { toast } from '@/shared/utils/UIHelper'
import { PromessaEventBus } from '@/components/events/promessaEventBus'

export default {
  name: 'ModalPromessas',
  props: {
    cardId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data () {
    return {
      promessas: [],
      filtroStatus: 'TODAS',
      isLoading: false,
      opcoesStatus: [
        { value: 'TODAS', text: 'Todas' },
        { value: 'QUEBRADA', text: 'Quebradas' },
        { value: 'VENCENDO', text: 'Vencendo' },
        { value: 'PENDENTE', text: 'Pendentes' },
        { value: 'CUMPRIDA', text: 'Cumpridas' },
        { value: 'CANCELADA', text: 'Canceladas' }
      ]
    }
  },
  computed: {
    promessasFiltradas () {
      if (this.filtroStatus === 'TODAS') {
        return this.promessas
      }

      return this.promessas.filter(p => p.statusVisual === this.filtroStatus)
    },

    contadores () {
      return {
        quebradas: this.promessas.filter(p => p.statusVisual === 'QUEBRADA').length,
        vencendo: this.promessas.filter(p => p.statusVisual === 'VENCENDO').length,
        pendentes: this.promessas.filter(p => p.statusVisual === 'PENDENTE').length,
        cumpridas: this.promessas.filter(p => p.statusVisual === 'CUMPRIDA').length,
        canceladas: this.promessas.filter(p => p.statusVisual === 'CANCELADA').length
      }
    }
  },
  methods: {
    showModal () {
      this.$refs['modal-promessas'].show()
    },

    hideModal () {
      this.$refs['modal-promessas'].hide()
    },

    onShow () {
      this.carregarPromessas()
    },

    onHide () {
      this.filtroStatus = 'TODAS'
    },

    async carregarPromessas () {
      if (!this.cardId) {
        return
      }

      this.isLoading = true

      try {
        const response = await PromessaRepository.getPromessasDetalhadasByCard(this.cardId)
        this.promessas = response.data || []
      } catch (error) {
        console.error('Erro ao carregar promessas:', error)
        toast.error('Erro ao carregar promessas')
      } finally {
        this.isLoading = false
      }
    },

    // MÉTODO REMOVIDO: marcarComoCumprida
    // Agora as promessas são marcadas como cumpridas AUTOMATICAMENTE
    // quando a parcela é liquidada (via importação de PARCELAS EM ABERTO)

    async cancelarPromessa (promessa) {
      const confirmacao = await this.$bvModal.msgBoxConfirm(
        `Tem certeza que deseja cancelar a promessa da parcela ${promessa.numeroParcela}?`,
        {
          title: 'Cancelar Promessa',
          okVariant: 'danger',
          okTitle: 'Sim, cancelar',
          cancelTitle: 'Não'
        }
      )

      if (!confirmacao) {
        return
      }

      try {
        await PromessaRepository.cancelarPromessa(promessa.promessaId)
        toast.success('Promessa cancelada!')
        this.carregarPromessas()
        this.$emit('promessa-atualizada')

        // Emite evento global para recarregar grade analítica
        PromessaEventBus.$emit('promessa-atualizada')
      } catch (error) {
        console.error('Erro ao cancelar promessa:', error)

        let mensagemErro = 'Erro ao cancelar promessa'
        if (error.data && typeof error.data === 'string') {
          mensagemErro = error.data
        }

        toast.error(mensagemErro)
      }
    },

    getStatusBadgeClass (status) {
      const classes = {
        'QUEBRADA': 'badge badge-danger',
        'VENCENDO': 'badge badge-warning',
        'PENDENTE': 'badge badge-info',
        'CUMPRIDA': 'badge badge-success',
        'CANCELADA': 'badge badge-secondary'
      }

      return classes[status] || 'badge badge-secondary'
    },

    getStatusIcon (status) {
      const icons = {
        'QUEBRADA': 'fas fa-times-circle',
        'VENCENDO': 'fas fa-clock',
        'PENDENTE': 'fas fa-spinner',
        'CUMPRIDA': 'fas fa-check-circle',
        'CANCELADA': 'fas fa-ban'
      }

      return icons[status] || 'fas fa-question-circle'
    },

    getStatusTexto (status) {
      const textos = {
        'QUEBRADA': 'Quebrada',
        'VENCENDO': 'Vencendo',
        'PENDENTE': 'Pendente',
        'CUMPRIDA': 'Cumprida',
        'CANCELADA': 'Cancelada'
      }

      return textos[status] || status
    },

    formatValue (value) {
      return Tools.formatMoneyBR(value)
    },

    formatDate (date) {
      if (!date) return '-'

      // Se a data vem como string
      if (typeof date === 'string') {
        // Extrair apenas a parte da data (antes do T ou espaço)
        const datePart = date.split('T')[0].split(' ')[0]

        // Verificar se está no formato yyyy-MM-dd
        if (datePart.includes('-')) {
          const [year, month, day] = datePart.split('-')
          return `${day}/${month}/${year}`
        }
      }

      // Fallback para objeto Date
      const d = new Date(date)
      return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    }
  }
}
</script>

<style scoped>
.filtros {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.badge-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* PADRÃO DA GRID ANALÍTICA COM FONTE MAIOR */
.table {
  th {
    font-weight: bold;
    font-size: 13px;
  }

  td {
    font-size: 13px;
    font-weight: normal;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.2s;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
}

.badge {
  font-size: 0.8rem;
  padding: 0.4rem 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.badge i {
  margin-right: 0;
}

.select-filtro {
  font-size: 14px !important;
}

.select-filtro option {
  font-size: 14px !important;
}

.label-filtro {
  font-size: 14px !important;
}
</style>



// WEBPACK FOOTER //
// src/views/private/crm/details/components/ModalPromessas.vue